/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Type-safe Admin REST API Client with Token Refresh Mutex (Task 11.1.3 & Sprint 12)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { getAdminDomains } from './urls';
import {
  useAdminAuthStore,
  ADMIN_AUTH_TOKEN_KEY,
  LEGACY_ADMIN_TOKEN_KEY,
} from '../stores/useAdminAuthStore';

export { ADMIN_AUTH_TOKEN_KEY, LEGACY_ADMIN_TOKEN_KEY };

/**
 * Global mutex promise to prevent concurrent duplicate refresh requests on Admin Portal.
 */
let refreshPromise: Promise<string | null> | null = null;

/**
 * Retrieves the current administrative access token from active state.
 */
export function getAdminToken(): string | null {
  return useAdminAuthStore.getState().token;
}

/**
 * Persists or clears the admin access token across storage keys.
 */
export function setAdminToken(token: string | null): void {
  if (token) {
    useAdminAuthStore.getState().updateAdminAccessToken(token);
  } else {
    useAdminAuthStore.getState().logout();
  }
}

export interface ApiFetchOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Helper to safely extract response payload (JSON or text).
 */
async function parseResponsePayload(response: Response): Promise<any> {
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return await response.json().catch(() => null);
  }
  return await response.text().catch(() => null);
}

/**
 * Executes transparent token refresh via edge auth worker when Remember Me is active.
 */
async function executeAdminTokenRefresh(): Promise<string | null> {
  const baseUrl = getAdminDomains().apiOrigin;
  const refreshToken = useAdminAuthStore.getState().refreshToken;

  try {
    const res = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        refreshToken: refreshToken || undefined,
      }),
    });

    if (!res.ok) {
      throw new Error(`Admin token refresh failed with HTTP ${res.status}`);
    }

    const data = (await res.json().catch(() => null)) as any;
    if (!data?.success || !data?.data?.accessToken) {
      throw new Error(data?.error?.message || 'Invalid refresh payload');
    }

    const newAccessToken = data.data.accessToken as string;
    const newRefreshToken = data.data.refreshToken as string | undefined;

    useAdminAuthStore.getState().updateAdminAccessToken(newAccessToken, newRefreshToken);
    return newAccessToken;
  } catch (err) {
    console.warn('[adminFetch] Background token refresh failed, terminating session:', err);
    useAdminAuthStore.getState().logout();

    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login?expired=1';
    }

    return null;
  }
}

/**
 * Universal authenticated fetch helper for Admin API with:
 * 1. Automatic Bearer token attachment
 * 2. 401 handling:
 *    - rememberMe === false: Immediate session clearance & redirect to /login?expired=1
 *    - rememberMe === true:  Mutex-locked auto-refresh and automatic retry
 */
export async function adminFetch<T = any>(endpoint: string, options: ApiFetchOptions = {}): Promise<T> {
  const { params, headers, ...customConfig } = options;
  const baseUrl = getAdminDomains().apiOrigin;

  let url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== '') {
        searchParams.append(key, String(val));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += (url.includes('?') ? '&' : '?') + queryString;
    }
  }

  const token = getAdminToken();

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  let response = await fetch(url, {
    ...customConfig,
    headers: {
      ...defaultHeaders,
      ...(headers as Record<string, string>),
    },
    credentials: customConfig.credentials || 'include',
  });

  if (response.status === 401) {
    // Avoid circular intercepting on auth endpoints themselves
    if (!url.includes('/api/v1/auth/login') && !url.includes('/api/v1/auth/refresh')) {
      const { rememberMe } = useAdminAuthStore.getState();

      // Branch 1: User did NOT check Remember Me -> Immediate logout & redirect
      if (!rememberMe) {
        useAdminAuthStore.getState().logout();
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login?expired=1';
        }
      } else {
        // Branch 2: User checked Remember Me -> Auto-refresh token with Mutex
        if (!refreshPromise) {
          refreshPromise = executeAdminTokenRefresh().finally(() => {
            refreshPromise = null;
          });
        }

        const newToken = await refreshPromise;
        if (newToken) {
          // Retry request with fresh access token
          const retryHeaders: Record<string, string> = {
            ...defaultHeaders,
            ...(headers as Record<string, string>),
            Authorization: `Bearer ${newToken}`,
          };

          response = await fetch(url, {
            ...customConfig,
            headers: retryHeaders,
            credentials: customConfig.credentials || 'include',
          });
        }
      }
    }
  }

  const data = await parseResponsePayload(response);

  if (!response.ok) {
    const errorCode = data?.error?.code || data?.code || `HTTP_${response.status}`;
    const errorMessage = data?.error?.message || data?.message || response.statusText || 'Unknown error occurred';
    throw new ApiError(response.status, errorCode, errorMessage, data?.error?.details || data?.details);
  }

  return data as T;
}

export const adminClient = {
  get: <T = any>(endpoint: string, params?: Record<string, any>, options?: ApiFetchOptions) =>
    adminFetch<T>(endpoint, { ...options, method: 'GET', params }),

  post: <T = any>(endpoint: string, body?: any, options?: ApiFetchOptions) =>
    adminFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  put: <T = any>(endpoint: string, body?: any, options?: ApiFetchOptions) =>
    adminFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  patch: <T = any>(endpoint: string, body?: any, options?: ApiFetchOptions) =>
    adminFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  delete: <T = any>(endpoint: string, options?: ApiFetchOptions) =>
    adminFetch<T>(endpoint, { ...options, method: 'DELETE' }),
};
